import { FormEvent, useEffect, useRef, useState } from "react"
import { t } from "../../i18n"
import { SageJobSite } from "../../Interfaces"
import { SageApiError } from "../../utils/sageApiError"
import { displayJobSiteCode, isWholeSageNumber, parseWholeSageNumber } from "../../utils/sageCodes"
import { sageJobSiteService } from "../../services/sageJobSiteService"
import CwFormModal, { CwField, cwInputClassName } from "./CwFormModal"

export default function JobSiteFormModal({
  isOpen,
  companyId,
  jobSite,
  onClose,
  onSaved,
}: {
  isOpen: boolean
  companyId: number
  jobSite?: SageJobSite | null
  onClose: () => void
  onSaved: () => void
}) {
  const editing = Boolean(jobSite?.id)
  const [jobNumber, setJobNumber] = useState("")
  const [name, setName] = useState("")
  const [address, setAddress] = useState("")
  const [city, setCity] = useState("")
  const [state, setState] = useState("")
  const [zip, setZip] = useState("")
  const [supervisor, setSupervisor] = useState("")
  const [jobNumberError, setJobNumberError] = useState("")
  const [nameError, setNameError] = useState("")
  const [formError, setFormError] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const numberRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!isOpen) return
    setJobNumber(jobSite ? displayJobSiteCode(jobSite) : "")
    setName(jobSite?.name || "")
    setAddress(jobSite?.address || "")
    setCity(jobSite?.city || "")
    setState(jobSite?.state || "")
    setZip(jobSite?.zip || "")
    setSupervisor(jobSite?.supervisor || "")
    setJobNumberError("")
    setNameError("")
    setFormError("")
    setSubmitting(false)
    const id = window.setTimeout(() => numberRef.current?.focus(), 0)
    return () => window.clearTimeout(id)
  }, [isOpen, jobSite])

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    const trimmedName = name.trim()
    const nextNumberError = !jobNumber.trim()
      ? t("jobSites.jobNumberRequired")
      : isWholeSageNumber(jobNumber)
        ? ""
        : t("jobSites.jobNumberInvalid")
    const nextNameError = trimmedName ? "" : t("jobSites.nameRequired")
    setJobNumberError(nextNumberError)
    setNameError(nextNameError)
    setFormError("")
    const parsedNumber = parseWholeSageNumber(jobNumber)
    if (nextNumberError || nextNameError || parsedNumber == null) return

    setSubmitting(true)
    try {
      const body = {
        jobNumber: parsedNumber,
        name: trimmedName,
        address: address.trim(),
        city: city.trim(),
        state: state.trim(),
        zip: zip.trim(),
        supervisor: supervisor.trim(),
      }
      if (editing && jobSite) {
        await sageJobSiteService.updateJobSite(companyId, jobSite.id, body)
      } else {
        await sageJobSiteService.createJobSite(companyId, body)
      }
      onSaved()
      onClose()
    } catch (error) {
      setFormError(error instanceof SageApiError && error.message
        ? error.message
        : t("jobSites.loadFailed"))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <CwFormModal
      isOpen={isOpen}
      title={editing ? t("jobSites.modalEditTitle") : t("jobSites.modalAddTitle")}
      submitLabel={t("common.save")}
      submitting={submitting}
      formError={formError}
      onClose={onClose}
      onSubmit={handleSubmit}
    >
      <CwField
        id="job-site-number"
        label={t("jobSites.jobNumberLabel")}
        helper={t("jobSites.jobNumberHelper")}
        error={jobNumberError}
      >
        <input
          ref={numberRef}
          id="job-site-number"
          name="jobNumber"
          inputMode="numeric"
          value={jobNumber}
          disabled={submitting}
          onChange={(event) => {
            setJobNumber(event.target.value)
            if (jobNumberError) setJobNumberError("")
          }}
          className={cwInputClassName}
        />
      </CwField>
      <CwField id="job-site-name" label={t("jobSites.nameLabel")} error={nameError}>
        <input
          id="job-site-name"
          name="name"
          value={name}
          disabled={submitting}
          onChange={(event) => {
            setName(event.target.value)
            if (nameError) setNameError("")
          }}
          className={cwInputClassName}
        />
      </CwField>
      <CwField id="job-site-address" label={t("jobSites.addressLabel")}>
        <input
          id="job-site-address"
          name="address"
          value={address}
          disabled={submitting}
          onChange={(event) => setAddress(event.target.value)}
          className={cwInputClassName}
        />
      </CwField>
      <div className="grid gap-5 sm:grid-cols-2">
        <CwField id="job-site-city" label={t("jobSites.cityLabel")}>
          <input
            id="job-site-city"
            name="city"
            value={city}
            disabled={submitting}
            onChange={(event) => setCity(event.target.value)}
            className={cwInputClassName}
          />
        </CwField>
        <CwField id="job-site-state" label={t("jobSites.stateLabel")}>
          <input
            id="job-site-state"
            name="state"
            value={state}
            disabled={submitting}
            onChange={(event) => setState(event.target.value)}
            className={cwInputClassName}
          />
        </CwField>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <CwField id="job-site-zip" label={t("jobSites.zipLabel")}>
          <input
            id="job-site-zip"
            name="zip"
            value={zip}
            disabled={submitting}
            onChange={(event) => setZip(event.target.value)}
            className={cwInputClassName}
          />
        </CwField>
        <CwField id="job-site-supervisor" label={t("jobSites.supervisorLabel")}>
          <input
            id="job-site-supervisor"
            name="supervisor"
            value={supervisor}
            disabled={submitting}
            onChange={(event) => setSupervisor(event.target.value)}
            className={cwInputClassName}
          />
        </CwField>
      </div>
    </CwFormModal>
  )
}
